/**
 * combat-balance.ts — single source of truth for every tuning knob and
 * data-shape helper the combat subsystem depends on (MECHANICS §1.2, §3).
 *
 * Mirrors the balance-detection.ts pattern: any number the combat pass
 * needs is pulled up here so iteration on feel doesn't require hunting
 * magic values in advance-combat.ts or the damage-chain helpers. Anything
 * in combat.ts, advance-combat.ts, or targeting.ts is structural and
 * requires a real code change.
 */

import type { WeaponDef, BalanceData } from '../state/types';

/**
 * Global multiplier converting a raw weapon `range` (authored in ships.json
 * as a small unit-less integer, e.g. 800 for Pulse Laser Mk 1) into local
 * in-system coords (the 0..1 frame that every ship position uses). Chosen
 * so a typical mid-range weapon reaches roughly the same order as a
 * Corvette's scan ring — keeps range tuning independent of the authored
 * data file, same idiom as VISION_RANGE_SCALE and SUBLIGHT_SPEED_SCALE.
 *
 * v3M10.1: migrated to state.balanceData.combat.weaponRangeScale.
 */

/**
 * Ticks the firing ship's slot waits between salvos — `ceil(1 / rateOfFire)`
 * clamped to at least 1. Exposed as a helper rather than a constant so the
 * math lives in one place and different weapons can share it; tests that
 * want to force rapid/slow fire just pass a hand-rolled rateOfFire.
 */
export function cooldownTicksFor(rateOfFire: number): number {
  if (!(rateOfFire > 0)) return Number.POSITIVE_INFINITY;
  return Math.max(1, Math.ceil(1 / rateOfFire));
}

/**
 * Concussion magazine refill — +1 ammo per tick up to the weapon's
 * `magazine` cap (MECHANICS §3.1.1 / §3.1.2). Kept as a named constant so
 * playtesters flipping the knob don't have to touch advance-combat.ts.
 *
 * v3M10.1: migrated to state.balanceData.combat.ammoRegenPerTick.
 */

/**
 * Per-damage-type multipliers applied *after* raw `damage` and *before* the
 * Shields → Armor% → Hull chain (combat.resolveDamage). MECHANICS §3
 * (2026-04-21 lock): Energy = 100% Shield / 90% Hull, Kinetic = 90% Shield
 * / 100% Hull, Concussion = 100% / 100% (blast-splash is a separate pass,
 * not currently wired).
 *
 * EMP does not deal HP damage — it applies disablement ticks instead — so
 * its multipliers are 0/0 and the combat pass short-circuits to the EMP
 * path before this table is consulted.
 *
 * v3M10.1: migrated to state.balanceData.combat.damageTypeModifiers.
 */
export function damageTypeModifiers(balance: BalanceData) {
  return balance.combat.damageTypeModifiers;
}

/**
 * Maps a ship template's *slot name* (e.g. `'Energy Pulse Weapon'`, as
 * authored in the `weaponLoadout[]` column of Ships.xlsx) to the
 * category+subType pair used to look weapons up in `GameState.weapons`.
 * Centralised here so adding a new slot type (once a future ship chassis
 * introduces one) is a one-line change with no string parsing to
 * accidentally misspell.
 */
export const SLOT_NAME_TO_CATEGORY: Readonly<
  Record<string, { category: WeaponDef['category']; subType: WeaponDef['subType'] }>
> = Object.freeze({
  'Energy Pulse Weapon': { category: 'Energy', subType: 'Pulse' },
  'Energy Beam Weapon': { category: 'Energy', subType: 'Beam' },
  'Kinetic Burst Weapon': { category: 'Kinetic', subType: 'Burst' },
  'Kinetic Railgun Weapon': { category: 'Kinetic', subType: 'Railgun' },
  'Concussion Missile Weapon': { category: 'Concussion', subType: 'Missile' },
  'Concussion Torpedo Weapon': { category: 'Concussion', subType: 'Torpedo' },
  'EMP Weapon': { category: 'Special', subType: 'EMP' },
});

/**
 * Resolve a loadout slot (e.g. `'Energy Pulse Weapon'`) to the *highest-Mk*
 * variant the owning empire has researched. Falls back to the Mk 1 variant
 * in the same category/subType — Mk 1 weapons have `researchCost = 0` and
 * no prerequisites in Tech_Tree.xlsx, so they're effectively always-
 * available baseline gear. Returns `undefined` only when the slot name
 * itself is unknown or no variants exist in the weapons catalogue.
 *
 * Pure — takes the weapons map and the empire's researched-tech set
 * explicitly. Safe to call from anywhere that already has those in hand
 * (combat pass, UI tooltips).
 */
export function resolveSlotWeapon(
  slotName: string,
  weapons: ReadonlyMap<string, WeaponDef>,
  researchedTechs: ReadonlySet<string>,
): WeaponDef | undefined {
  const cat = SLOT_NAME_TO_CATEGORY[slotName];
  if (!cat) return undefined;

  // Walk all weapons and keep the highest-Mk one that either (a) has been
  // researched or (b) is the Mk 1 fallback. Single pass — weapons table is
  // ~40 rows, never hot enough to need indexing.
  let best: WeaponDef | undefined;
  let fallback: WeaponDef | undefined;
  for (const w of weapons.values()) {
    if (w.category !== cat.category || w.subType !== cat.subType) continue;
    if (w.mk === 1) {
      // Prefer the lowest Mk 1 even if multiple exist (tech tree authors
      // could add flavour variants). This branch never runs twice in v1.
      if (!fallback || w.mk < fallback.mk) fallback = w;
    }
    if (researchedTechs.has(w.name)) {
      if (!best || w.mk > best.mk) best = w;
    }
  }
  return best ?? fallback;
}
